import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const faqs = [{
  question: '¿Y si no estoy satisfecho/a?',
  answer: 'Tu satisfacción es mi prioridad. Si considerás que tu pedido no te brindó la claridad o la orientación que buscabas, contactame y lo solucionaré. Puedo ofrecerte orientación adicional, aclaraciones o una lectura complementaria sin costo alguno. En caso de que aún no se logre tu satisfacción, se considerará un reembolso parcial o total. Además, contás con una garantía de 7 días: si dentro de ese plazo no ves resultados, te devuelvo tu dinero sin preguntas. En más de 10 años de experiencia, menos del 0.5% de mis clientas han necesitado usar este servicio. Estás en buenas manos. Se aplican términos y condiciones.'
}, {
  question: '¿Qué métodos de pago aceptan?',
  answer: 'Acepto todas las principales tarjetas de crédito, débito y Mercado Pago a través de mi plataforma de pago segura con cifrado SSL. Tus datos de pago están protegidos con medidas de seguridad bancarias y nunca se almacenan en mis servidores.'
}, {
  question: '¿Ofrecen seguimiento posterior?',
  answer: 'Sí, y esto es lo que me distingue. Recibirás apoyo continuo ilimitado, porque una sola lectura es solo el comienzo. Si surgen nuevas preguntas, las circunstancias cambian o necesitás mayor claridad sobre lo que he compartido, estoy aquí. La mayoría de mis clientas se mantienen en contacto durante años porque contar con una guía espiritual de confianza durante los cambios de la vida es algo que nunca quieren dejar de tener.'
}, {
  question: '¿Mi información se mantiene privada?',
  answer: 'Completamente. Todo lo que compartas conmigo se quedará entre nosotras, siempre. Durante más de 10 años, he guardado con absoluta confidencialidad los miedos, secretos y preguntas más profundas de mis clientas. Tu información personal y nuestras comunicaciones están protegidas mediante cifrado SSL y nunca se comparten con nadie. Lo que me cuentes se quedará conmigo. Es un compromiso personal, no solo una política.'
}, {
  question: '¿En cuánto tiempo recibiré mi pedido?',
  answer: 'Tras realizar tu pedido, recibirás un correo electrónico de confirmación. Simplemente respondé con algunos datos personales: tu fecha de nacimiento, tu situación actual y cualquier otra información que quieras compartir. Una vez recibida tu respuesta, prepararé personalmente tu pedido y te lo enviaré directamente a tu correo electrónico. La mayoría de los pedidos se completan en 24 horas. Si deseás una entrega más rápida, podés optar por el envío exprés al finalizar la compra.'
}, {
  question: '¿Qué tan precisas son sus lecturas?',
  answer: 'Mi índice de precisión documentado supera el 89% en más de 10 años de práctica. Pero esto es lo que realmente significa: solo comparto lo que veo con claridad, nunca lo que la gente quiere oír. Algunos videntes exageran las expectativas para hacerte sentir bien. Yo te digo la verdad porque eso es lo que realmente te ayuda. Mis clientas me dicen constantemente que describo detalles específicos que me sería imposible conocer. No puedo garantizar que te guste lo que veo, pero sí puedo garantizar que recibirás la verdad.'
}];
function FAQItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border border-white/5 rounded-2xl overflow-hidden bg-[#111] hover:border-[#D4AF37]/20 transition-colors" data-id="element-105">
      <button onClick={onToggle} className="w-full flex items-center justify-between gap-4 p-6 sm:p-8 text-left" data-id="element-106">
        <h3 className="font-playfair text-lg sm:text-xl text-white pr-4" data-id="element-107">
          {question}
        </h3>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.3
      }} className="flex-shrink-0" data-id="element-108">
          <ChevronDown className="w-5 h-5 text-[#D4AF37]" data-id="element-109" />
        </motion.div>
      </button>
      <AnimatePresence initial={false} data-id="element-110">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-111">
            <div className="px-6 sm:px-8 pb-6 sm:pb-8 pt-0" data-id="element-112">
              <div className="w-full h-px bg-white/5 mb-6" data-id="element-113" />
              <p className="font-inter text-gray-400 text-sm sm:text-base leading-relaxed" data-id="element-114">
                {answer}
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <section className="w-full py-20 px-4 bg-[#0a0a0a]" data-id="element-115">
      <div className="max-w-3xl mx-auto" data-id="element-116">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="font-playfair text-3xl sm:text-4xl text-center text-white mb-4" data-id="element-117">
          Preguntas{' '}
          <span className="text-gradient-gold italic" data-id="element-118">Frecuentes</span>
        </motion.h2>
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.1
      }} className="font-inter text-gray-400 text-center mb-12 text-sm sm:text-base" data-id="element-119">
          Todo lo que necesitás saber antes de hacer tu pedido
        </motion.p>

        <div className="flex flex-col gap-3" data-id="element-120">
          {faqs.map((faq, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: idx * 0.05
        }} data-id="element-121">
              <FAQItem question={faq.question} answer={faq.answer} isOpen={openIndex === idx} onToggle={() => setOpenIndex(openIndex === idx ? null : idx)} data-id="element-122" />
            </motion.div>)}
        </div>
      </div>
    </section>;
}